import { Currency } from 'utils/Currency';
import { COUNTRY_STORE_CONFIG } from './countryStoreConfig';

export type { StoreConfig } from './countryStoreConfig';
export { COUNTRY_STORE_CONFIG, getStoreConfig } from './countryStoreConfig';
export { COUNTRY_NAME_FALLBACK, getCountryName } from './countryNames';

/**
 * Complete currency symbol mapping for all supported currencies
 */
const CURRENCY_SYMBOL_MAP: { [key in Currency]: string } = {
  [Currency.inr]: '₹',
  [Currency.eur]: '€',
  [Currency.gbp]: '£',
  [Currency.aed]: 'AED ',
  [Currency.usd]: '$',
  [Currency.ch]: 'CHF ',
  [Currency.hk_usd]: '$',
  [Currency.aud]: 'A$',
  [Currency.bgn]: 'лв',
  [Currency.brl]: 'R$',
  [Currency.cad]: 'C$',
  [Currency.clp]: '$',
  [Currency.cny]: '¥',
  [Currency.cop]: '$',
  [Currency.czk]: 'Kč',
  [Currency.dkk]: '$',
  [Currency.egp]: '£',
  [Currency.hkd]: '$',
  [Currency.huf]: 'Ft',
  [Currency.idr]: 'Rp',
  [Currency.ils]: '₪',
  [Currency.jpy]: '¥',
  [Currency.krw]: '₩',
  [Currency.kzt]: '₸',
  [Currency.mxn]: 'MXN$',
  [Currency.myr]: 'RM',
  [Currency.ngn]: '₦',
  [Currency.nok]: '$',
  [Currency.nzd]: '$',
  [Currency.pen]: 'S/',
  [Currency.php]: '₱',
  [Currency.pkr]: 'Rs',
  [Currency.pln]: 'zł',
  [Currency.qar]: 'QR',
  [Currency.ron]: 'lei',
  [Currency.rub]: '₽',
  [Currency.sar]: 'SAR ',
  [Currency.sek]: '$',
  [Currency.sgd]: '$',
  [Currency.thb]: '฿',
  [Currency.try]: '₺',
  [Currency.twd]: '$',
  [Currency.tzs]: 'TSh',
  [Currency.vnd]: '₫',
  [Currency.zar]: 'R',
};

/**
 * Get currency symbol by currency enum directly
 */
export const getCurrencySymbolFromEnum = (currency: Currency): string => {
  return CURRENCY_SYMBOL_MAP[currency] || '$';
};

/**
 * Get currency symbol from currency string (case-insensitive)
 */
export const getCurrencySymbolFromString = (currencyCode: string): string => {
  const normalizedCode = currencyCode.toLowerCase() as Currency;
  return CURRENCY_SYMBOL_MAP[normalizedCode] || '$';
};

/**
 * Product types available in the Ultrahuman ecosystem
 */
export type ProductType = 'ring' | 'ring_pro' | 'm1' | 'home' | 'rare' | 'shop' | 'x';

/**
 * Get all supported country codes.
 * Optionally filter by product availability.
 *
 * @param product - Optional product type to filter by
 * @returns Array of supported country codes
 *
 * @example
 * getSupportedCountryCodes() // Returns all countries
 * getSupportedCountryCodes('ring') // Returns only countries that support Ring
 */
export const getSupportedCountryCodes = (product?: ProductType): string[] => {
  const allCodes = Object.keys(COUNTRY_STORE_CONFIG);

  if (!product) {
    return allCodes;
  }

  return allCodes.filter((code) => isProductAvailable(code, product));
};

/**
 * Check if a country code is supported.
 *
 * @param countryCode - ISO 3166-1 alpha-2 country code
 * @returns true if country is configured in COUNTRY_STORE_CONFIG
 *
 * @example
 * isCountrySupported('MX') // true
 * isCountrySupported('ZZ') // false
 */
export const isCountrySupported = (countryCode: string): boolean => {
  return countryCode.toUpperCase() in COUNTRY_STORE_CONFIG;
};

/**
 * Check if a specific product is available in a country.
 * Products are assumed to be available unless explicitly marked as unsupported.
 *
 * @param countryCode - ISO 3166-1 alpha-2 country code
 * @param product - Product type to check
 * @returns true if product is available (default), false if explicitly unsupported
 *
 * @example
 * isProductAvailable('MX', 'ring') // true (default)
 * isProductAvailable('MX', 'm1') // false (if explicitly marked unsupported)
 */
export const isProductAvailable = (
  countryCode: string,
  product: ProductType,
): boolean => {
  const config = COUNTRY_STORE_CONFIG[countryCode.toUpperCase()] || COUNTRY_STORE_CONFIG['ROW'];

  // Default to true (supported) unless explicitly marked as unsupported
  return !config.unsupportedProducts?.[product];
};

/**
 * True when the visitor's geo country cannot be sold Ring AIR (config:
 * `unsupportedProducts.ring`, today US and PR). Production only: outside
 * production the geo falls back to NEXT_PUBLIC_DEFAULT_COUNTRY (default
 * 'US'), which would otherwise block every dev/preview visitor. Mirrors the
 * production gate in lib/region.server.ts.
 *
 * @param geoCountry - `detectedRegionFromGeoIP` from useRegionLocale()
 */
export const isRingAirBlockedForGeo = (
  geoCountry: string | null | undefined,
): boolean => {
  if (process.env.NEXT_PUBLIC_ENVIRONMENT !== 'PRODUCTION') return false;
  if (!geoCountry) return false;
  return !isProductAvailable(geoCountry, 'ring');
};

/**
 * Check if a country uses Multi-Country Fulfillment (MCF) for M1 products.
 * MCF is used for EU countries with special fulfillment requirements.
 *
 * @param countryCode - ISO 3166-1 alpha-2 country code
 * @returns true if country uses MCF for M1
 *
 * @example
 * isMCFCountry('DE') // true (Germany uses MCF)
 * isMCFCountry('US') // false
 */
export const isMCFCountry = (countryCode: string): boolean => {
  const config = COUNTRY_STORE_CONFIG[countryCode.toUpperCase()];
  return config?.m1Config?.mcf === true;
};
